import { ProductBundle } from './Product'
import { PrescriptionFile } from './PrescriptionFile'
import { ClaimData, Patient, PolicyCoverage } from './ClaimData'
import { Price } from './Price'

export enum ClaimRecordStatus {
  CREATED = 'CREATED',
  UPDATED = 'UPDATED',
  PRESCRIPTIONS_REQUIRED = 'PRESCRIPTIONS_REQUIRED',
  PRESCRIPTIONS_AUTO_APPROVED = 'PRESCRIPTIONS_AUTO_APPROVED',
  PRESCRIPTIONS_APPROVED = 'PRESCRIPTIONS_APPROVED',
  PRESCRIPTIONS_DISAPPROVED = 'PRESCRIPTIONS_DISAPPROVED',
  PRESCRIPTIONS_FOR_REVIEW = 'PRESCRIPTIONS_FOR_REVIEW',
  PRESCRIPTIONS_IN_REVIEW = 'PRESCRIPTIONS_IN_REVIEW',
  QUOTE_REQUIRED = 'QUOTE_REQUIRED',
  QUOTE_REQUESTED = 'QUOTE_REQUESTED',
  QUOTE_PENDING = 'QUOTE_PENDING',
  QUOTE_SYSTEM_REFUSED_EVENT_SENT = 'QUOTE_SYSTEM_REFUSED_EVENT_SENT',
  READY_TO_CLAIM = 'READY_TO_CLAIM',
  CLAIM_SUBMITTED = 'CLAIM_SUBMITTED',
  CLAIM_RETRY = 'CLAIM_RETRY',
  CLAIM_RETRY_FAILED = 'CLAIM_RETRY_FAILED',
  CLAIM_ACCEPTED = 'CLAIM_ACCEPTED',
  CLAIM_ACKNOWLEDGED = 'CLAIM_ACKNOWLEDGED',
  CLAIM_REJECTED = 'CLAIM_REJECTED',
  CLAIM_NULLIFY_EVENT_SENT = 'CLAIM_NULLIFY_EVENT_SENT',
  CLAIM_NULLIFY_REQUIRED = 'CLAIM_NULLIFY_REQUIRED',
  CLAIM_NULLIFY_SENT = 'CLAIM_NULLIFY_SENT',
  CLAIM_NULLIFIED = 'CLAIM_NULLIFIED',
  CLAIM_NULLIFY_REJECTED = 'CLAIM_NULLIFY_REJECTED',
  CLAIM_NULLIFY_FAILED = 'CLAIM_NULLIFY_FAILED',
  FOR_MANUAL_NULLIFY = 'FOR_MANUAL_NULLIFY',
  CLAIM_NULLIFIED_MANUALLY = 'CLAIM_NULLIFIED_MANUALLY',
  CLAIM_REFUNDED = 'CLAIM_REFUNDED',
  CLAIM_INVOICED = 'CLAIM_INVOICED',
  CLAIM_CLOSED = 'CLAIM_CLOSED',
  RESTRICTED = 'RESTRICTED',
  NOT_RESTRICTED = 'NOT_RESTRICTED',
  INSURANCE_MEMBER_REQUIRED = 'INSURANCE_MEMBER_REQUIRED',
  INSURANCE_MEMBER_FOUND = 'INSURANCE_MEMBER_FOUND',
  INSURANCE_MEMBER_NOT_FOUND = 'INSURANCE_MEMBER_NOT_FOUND',
  MULTIPLE_INSURANCE_MEMBERS_FOUND = 'MULTIPLE_INSURANCE_MEMBERS_FOUND',
  NO_BENEFITS_AVAILABLE = 'NO_BENEFITS_AVAILABLE',
  READY_TO_QUOTE = 'READY_TO_QUOTE',
  CLOSED = 'CLOSED',
  CLOSE_REQUIRED = 'CLOSE_REQUIRED',
  DELETED = 'DELETED',
  DELETE_IN_PROGRESS = 'DELETE_IN_PROGRESS', // consider same as DELETED
  NONE = 'NONE',
}

export enum CustomerFacingClaimRecordStatus {
  DEFAULT = 'DEFAULT',
  IN_PROGRESS = 'IN_PROGRESS',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  ACKNOWLEDGED_NULLIFIED = 'ACKNOWLEDGED_NULLIFIED',
  NULLIFIED = 'NULLIFIED',
  TIMEOUT_RETRY = 'TIMEOUT_RETRY',
  LIMIT_REACHED = 'LIMIT_REACHED',
  SERVICE_OFFLINE = 'SERVICE_OFFLINE',
  NULLIFY_REJECTED = 'NULLIFY_REJECTED',
  NO_CLAIM_RECORD = 'NO_CLAIM_RECORD',
}

export type ClaimRecord = {
  id: string
  cartId: string
  sessionId: string
  customerNumber: string
  customerEmail: string
  customerPhone?: string
  customerName?: {
    firstName: string | undefined
    lastName: string | undefined
  }
  eligibleProducts?: ProductBundle[]
  prescriptions?: PrescriptionFile[]
  status: ClaimRecordStatus
  claimData: ClaimData
  lastModified: string
  locale?: string
  ecommerce: string
  lifeCycle: ClaimRecordLifeCycle
  orderSubtotal?: Price
  shippingCosts?: Price
}

export enum ClaimRecordLifeCycle {
  CROSS_SESSION = 'CROSS_SESSION',
  SESSION_ONLY = 'SESSION_ONLY',
}

export enum ActiveClaimCheckStatus {
  PENDING = 'PENDING',
  COMPLETE = 'COMPLETE',
  NONE = 'NONE',
}

export type BaseClaimData = {
  primaryCoverage?: Partial<PolicyCoverage>
  patient?: Patient
}

export type CreateClaimRecordPayload = {
  locale: string
  ecommerce: string | null
  sessionId: string
  customerNumber: string
  customerEmail?: string
  cartId?: string
  customerPhone?: string
  customerName?: {
    firstName: string | undefined
    lastName: string | undefined
  }
  eligibleProducts?: ProductBundle[]
  orderSubtotal?: Price
  shippingCosts?: Price
  claimData?: BaseClaimData
}

export type CreateClaimRecordResult = {
  id: string
}

export type ClaimRecordEligibleProducts = {
  id: string
  eligibleProducts: ProductBundle[]
  orderSubtotal?: Price
  shippingCosts?: Price
}

export type EligibleProductsResult = {
  eligibleProducts: ProductBundle[]
}

export type NullifyResponse = {
  claimRecord: ClaimRecord
}

export enum NullifyReason {
  ALTERED_DECISION = 'CUSTOMER_INITIATED',
  ENTERED_IN_ERROR = 'CART_UPDATE',
}

export enum NullifyInitiator {
  CUSTOMER = 'CUSTOMER',
  SYSTEM = 'SYSTEM',
}

export type NullifyArgs = {
  claimRecordId: string
  reason: NullifyReason
  initiatedBy: NullifyInitiator
  isLastAttempt?: boolean // not sent to API
}

export type NullifyOptions = {
  claimRecordStatus: ClaimRecordStatus
  onComplete?: () => void
  onError?: (error?: unknown) => void
  onFinally?: () => void
  isLastAttempt?: boolean
} & NullifyArgs

export type DeleteArgs = {
  customerNumber: string
  claimRecordId: string
  cartId?: string
  customerEmail?: string
}

export type ClaimInformation = {
  claimId?: string
}

export type NullifyCountArgs = {
  customerNumber: string
}

export type SubmitClaimArgs = {
  claimRecordId: string
  termsAndConditionsAccepted: boolean
}

export type FetchAttachmentArgs = {
  claimId: string
}

export type UrlResult = {
  url?: string
}

export type ActiveDataResult = {
  id: string
}

export type GetActiveDataArgs = {
  customerNumber: string
}

export type SyncInsuranceMemberPayload = {
  groupId?: string
  memberId?: string
}

export type SyncInsuranceMemberArgs = {
  claimRecordId: string
  payload?: SyncInsuranceMemberPayload
}

export type AutoNullifyStatus = {
  isInProgress?: boolean
  isSkipPolling?: boolean
}
